import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { type ContentMeta } from '@/lib/content';
import { formatDate } from '@/lib/format';
import EmptyState from '@/components/EmptyState';
import ResourceFilters from '@/components/ResourceFilters';
import { EMPTY_FILTERS, filterResources, resourceFacets, resourceProfile } from '@/lib/resource-profiles';
import '@/components/reading-experience.css';

interface ContentIndexProps {
  items: ContentMeta[];
  emptyHeading?: string;
  emptyBody?: string;
  /** Show the tag chips (default true). */
  tags?: boolean;
}

const Arrow = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">
    <path d="M4 12h15m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

// An editorial index. All entries remain in the initial HTML; filtering is a
// progressive enhancement, so the complete library is available to crawlers.
const ContentIndex = ({
  items,
  emptyHeading = 'Guides are being added.',
  emptyBody = 'This section is being written now. Until it is, an engineer can answer the question directly.',
  tags = true,
}: ContentIndexProps) => {
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const filteredItems = useMemo(() => filterResources(items, filters), [items, filters]);
  const facets = useMemo(() => resourceFacets(items), [items]);
  const filtering = Object.values(filters).some(Boolean);

  if (!items.length) return <EmptyState heading={emptyHeading} body={emptyBody} />;
  return (
    <div className="ecm-library">
      {items.length >= 5 && <ResourceFilters value={filters} onChange={setFilters} facets={facets} count={filteredItems.length} total={items.length} noun="articles" />}
      <ul className={`ecm-library-grid${filteredItems.length <= 4 ? ' ecm-library-grid--small' : ''}`}>
        {filteredItems.map((c, index) => (
          <li key={c.path} className={index === 0 && !filtering && items.length > 2 ? 'ecm-library-featured' : undefined}>
            <article className="ecm-library-card">
              <Link to={c.path}>
                <div className="ecm-library-card-top">
                  <span>{resourceProfile(c.path).type}</span>
                  <Arrow />
                </div>
                <div className="ecm-library-card-body">
                  <h3>{c.title}</h3>
                  <p>{c.description}</p>
                </div>
                {tags && c.tags.length > 0 && (
                  <ul className="ecm-library-tags" aria-label="Topics">
                    {c.tags.map((t) => <li key={t}>{t}</li>)}
                  </ul>
                )}
                <div className="ecm-library-card-meta">
                  <time dateTime={c.updated}>{formatDate(c.updated)}</time>
                  {c.readingTime && <span>{c.readingTime} read</span>}
                </div>
              </Link>
            </article>
          </li>
        ))}
      </ul>
      {filteredItems.length === 0 && (
        <p className="ecm-library-no-results">No articles match this combination. Clear a filter or try a broader search.</p>
      )}
    </div>
  );
};

export default ContentIndex;
